// IngestHealthBanner — global strip rendered above the main content when
// the in-container ingest sidecar is unreachable. Visible to every signed-
// in user (not admin-gated) because a dead ingest stalls every player's
// crash/exception/bug-report intake, which is more important to surface
// fast than to keep tidy.
//
// Polls /api/health/ingest-up every 15s. The endpoint itself caches for
// 2s on the server, so a tab regaining focus + repolling can't dogpile
// the loopback probe.

import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { apiPublic } from '@/lib/api';
import { PATHS } from '@/lib/paths';

const POLL_INTERVAL_MS = 15 * 1000;

export function IngestHealthBanner() {
  const { user } = useAuth();
  const signedIn = !!user;
  const [up, setUp] = useState<boolean | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!signedIn) return;
    let cancelled = false;

    const tick = async () => {
      try {
        const res = await apiPublic.raw<{ ingestUp?: boolean }>(PATHS.healthIngestUp, { ignoreResponseError: true });
        if (!res.ok) {
          if (!cancelled) setUp(false);
          return;
        }
        if (!cancelled) setUp(!!res._data?.ingestUp);
      } catch {
        if (!cancelled) setUp(false);
      }
    };

    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [signedIn]);

  if (!signedIn) return null;
  if (up !== false) return null;
  if (dismissed) return null;

  return (
    <div
      role="alert"
      className="flex items-center justify-between gap-3 px-4 py-2 text-sm bg-red-600 text-white"
    >
      <div className="flex-1 min-w-0">
        <strong className="mr-2">Ingest service is down:</strong>
        <span>
          New crashes, exceptions, ANRs, and bug reports are queuing on
          devices until the in-container ingest sidecar comes back. Existing
          dashboard data is unaffected.
        </span>
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="rounded border border-white/40 px-2 py-0.5 text-xs hover:bg-white/10"
        aria-label="Dismiss ingest banner"
        title="Hide until next reload"
      >
        Dismiss
      </button>
    </div>
  );
}
